import React from "react";
import { useTable, useSortBy } from "react-table";
import { ArrowUpIcon, ArrowDownIcon } from "@heroicons/react/20/solid";
import { TableSkeleton } from "../loading/tableSkeleton";
import { smartCompare } from "../../utils/tableSort";

// Normalise the incoming `defaultSort` prop into react-table's sortBy shape.
// Accepts a column id string ("name") or an object ({ id, desc }).
function toSortBy(defaultSort) {
  if (!defaultSort) return [];
  if (typeof defaultSort === "string") return [{ id: defaultSort, desc: false }];
  return [{ id: defaultSort.id, desc: !!defaultSort.desc }];
}

function Table({
  data,
  columns,
  emptyState,
  isLoading = false,
  skeletonRows = 5,
  defaultSort,
}) {
  // Data (text/date) columns are sortable by default; action columns — those
  // with an empty Header, like the trailing delete button — opt out. Callers
  // can still override either way with `disableSortBy` on the column.
  const sortableColumns = React.useMemo(
    () =>
      columns.map((column) => {
        const hasHeader = Boolean(column.Header);
        return {
          sortType: smartCompare,
          disableSortBy: !hasHeader,
          ...column,
        };
      }),
    [columns],
  );

  const tableInstance = useTable(
    {
      columns: sortableColumns,
      data: data || [],
      disableSortRemove: true, // toggling a column flips asc/desc, never clears
      initialState: { sortBy: toSortBy(defaultSort) },
    },
    useSortBy,
  );

  const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow } =
    tableInstance;

  return (
    <div>
      <table {...getTableProps()} className="min-w-full text-sm h-full">
        <thead>
          {headerGroups.map((headerGroup) => (
            <tr {...headerGroup.getHeaderGroupProps()} className="h-full">
              {headerGroup.headers.map((column) => (
                <th
                  {...column.getHeaderProps(
                    column.getSortByToggleProps({ title: undefined }),
                  )}
                  className={`px-3 py-3 text-left text-xxsm font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider ${
                    column.canSort
                      ? "cursor-pointer select-none transition-colors hover:text-gray-700 dark:hover:text-gray-200"
                      : ""
                  }`}
                >
                  <span className="inline-flex items-center gap-1">
                    {column.render("Header")}
                    {column.isSorted &&
                      (column.isSortedDesc ? (
                        <ArrowDownIcon className="h-3 w-3 shrink-0" />
                      ) : (
                        <ArrowUpIcon className="h-3 w-3 shrink-0" />
                      ))}
                  </span>
                </th>
              ))}
            </tr>
          ))}
        </thead>
        <tbody
          {...getTableBodyProps()}
          key={isLoading ? "loading" : "loaded"}
          className={`h-full${!isLoading ? " animate-fade-in" : ""}`}
        >
          {isLoading ? (
            Array.from({ length: skeletonRows }).map((_, rowIdx) => (
              <tr key={rowIdx} className="border-t border-gray-100 dark:border-gray-800">
                {headerGroups[0].headers.map((_, colIdx) => (
                  <td key={colIdx} className="px-3 py-1">
                    <TableSkeleton />
                  </td>
                ))}
              </tr>
            ))
          ) : rows.length === 0 && emptyState ? (
            <tr>
              <td colSpan={columns.length} className="px-3 py-8 text-center">
                {emptyState}
              </td>
            </tr>
          ) : (
            rows.map((row) => {
              prepareRow(row);
              return (
                <tr {...row.getRowProps()} className="h-full border-t border-gray-100 dark:border-gray-800">
                  {row.cells.map((cell, cellIndex) => {
                    const isFirst = cellIndex === 0;
                    const isLast = cellIndex === row.cells.length - 1;
                    return (
                      <td
                        {...cell.getCellProps()}
                        className={`text-gray-400 dark:text-gray-500 px-3 py-1 relative h-full ${
                          cell.column.nowrap ? "whitespace-nowrap" : "whitespace-normal"
                        } ${isFirst ? "rounded-l-lg" : ""} ${isLast ? "rounded-r-lg" : ""}`}
                      >
                        {cell.render("Cell")}
                      </td>
                    );
                  })}
                </tr>
              );
            })
          )}
        </tbody>
      </table>
    </div>
  );
}

export { Table };
